import { aspenApiReducer, AspenApiState } from '@om1/aspen-api/state'
import { platformConfigReducer, PlatformConfigState } from '@om1/platform-config/state'
import { createNotificationSaga, notificationActions, notificationReducer, NotificationState } from '@om1/platform-notifications'
import { createTrackingSaga } from '@om1/platform-tracking'
import { configureStore } from '@reduxjs/toolkit'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { connectRouter, RouterState } from 'connected-react-router'
import { LocationState } from 'history'
import { OptionsObject, SnackbarKey, SnackbarMessage } from 'notistack'
import { AnyAction, applyMiddleware, CombinedState, combineReducers, Middleware, Reducer } from 'redux'
import dynamicMiddlewares from 'redux-dynamic-middlewares'
import { SagaMiddleware } from 'redux-saga'
import { all, put, spawn } from 'redux-saga/effects'
import keepAlive from './keep-alive'
import { languageReducer } from './shared/providers/internationalization/language-reducer'
import { LanguageState } from './shared/providers/internationalization/language-state'

export const createReducer = (history: any) =>
    combineReducers({
        router: connectRouter(history),
        language: languageReducer,
        notification: notificationReducer,
        aspenApi: aspenApiReducer,
        platformConfig: platformConfigReducer
    })

export const appReducer = createReducer(window.history)

export type AspenState = ReturnType<typeof appReducer>

export function createAppStore(
    reducer: Reducer<
        CombinedState<{
            router: RouterState<LocationState>
            language: LanguageState
            notification: NotificationState
            aspenApi: AspenApiState
            platformConfig: PlatformConfigState
        }>,
        AnyAction
    >,
    sagaMiddleware: SagaMiddleware,
    ...additionalMiddlewares: Middleware[]
) {
    const middlewares = [dynamicMiddlewares, ...additionalMiddlewares]
    return configureStore({
        reducer: reducer,
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: { ignoredActions: ['@@notifications/success', '@@notifications/error', '@@notifications/warning'] }
            }).concat(sagaMiddleware),
        enhancers: [applyMiddleware(...middlewares)]
    })
}

export type AppStore = ReturnType<typeof createAppStore>

export function createOnFail(sagaName: string) {
    function* onFail(error: any) {
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        const errorMessage = `${sagaName} module has produced too many uncaught errors and is shutting down, these exceptions should be handled.`
        yield console.warn(errorMessage)
        yield console.error(error)
        yield put(notificationActions.error(errorMessage))
    }
    function* onEachError(_next: any, error: any, _saga: any) {
        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
        const errorMessage = `${sagaName} module has produced an error and is restarting, these exceptions should be handled.`
        yield console.warn(errorMessage)
        yield console.error(error)
        yield put(notificationActions.warning(errorMessage))
    }
    return { onFail, onEachError, disableWarnings: true }
}

export function createRootSaga(enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey, analytics?: AnalyticsBrowser) {
    const notificationSaga = createNotificationSaga(enqueueSnackbar)

    let sagas = [{ saga: notificationSaga, name: 'Notifications' }]

    if (analytics) {
        const trackingSaga = createTrackingSaga(analytics)
        sagas.push({ saga: trackingSaga, name: 'User Tracking' })
    }

    return function* () {
        yield all(sagas.map((saga) => spawn(keepAlive(saga.saga, createOnFail(saga.name)))))
    }
}
